import { Content } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextLink } from '@prismicio/next';
import { PlainRightArrow } from '@/assets/PlainRightArrow';
import {
  HeadlineCtaCenteredSliceDefault,
  HeadlineCtaCenteredSliceWithDescription,
} from '../../../prismicio-types';
import WithDescription from '@/slices/HeadlineCtaCentered/variants/WithDescription';
import ClientTrust from '@/slices/HeadlineCtaCentered/variants/ClientTrust';
import ServicesCta from '@/slices/HeadlineCtaCentered/variants/ServicesCta';

enum SLICE_VARIATION {
  default = 'default',
  withDescription = 'withDescription',
  clientTrust = 'clientTrust',
  servicesDark = 'servicesDark',
}

type DefaultSlice = HeadlineCtaCenteredSliceDefault & { slice_type: string };
type WithDescriptionSlice = HeadlineCtaCenteredSliceWithDescription & {
  slice_type: string;
};

export type HeadlineCtaCenteredProps =
  SliceComponentProps<Content.HeadlineCtaCenteredSlice>;

const HeadlineCtaCentered = ({ slice }: HeadlineCtaCenteredProps) => {
  const variation = slice.variation as string;
  if (variation === SLICE_VARIATION.default) {
    return defaultVariation(slice as DefaultSlice);
  }
  if (variation === SLICE_VARIATION.withDescription) {
    return withDescription(slice as WithDescriptionSlice);
  }
  if (variation === SLICE_VARIATION.clientTrust) {
    return (
      <ClientTrust
        slice={slice as unknown as Parameters<typeof ClientTrust>[0]['slice']}
      />
    );
  }
  if (variation === SLICE_VARIATION.servicesDark) {
    return (
      <ServicesCta
        slice={slice as unknown as Parameters<typeof ServicesCta>[0]['slice']}
      />
    );
  }
  return null;
};

const defaultVariation = (slice: DefaultSlice) => {
  const {
    headline,
    primary_cta: cta,
    cta_label: ctaLabel,
    background_image: bgImage,
  } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      style={bgImage?.url ? { backgroundImage: `url(${bgImage.url})` } : {}}
      className="bg-center bg-no-repeat bg-cover h-full py-12 md:py-24 flex items-center justify-center"
    >
      <div className="container mx-auto px-4 sm:px-6 md:px-48 text-white text-center">
        <div className="flex flex-col items-center gap-y-7">
          <div className="font-bold text-[40px] leading-[120%]">
            <PrismicRichText field={headline} />
          </div>
          <PrismicNextLink
            field={cta}
            className="button-squared"
            style={{ backgroundColor: '#05F36C', color: '#000' }}
          >
            <div className="flex flex-row items-center">
              <span>{ctaLabel}</span>
              <PlainRightArrow fill="black" arrowFill="black" />
            </div>
          </PrismicNextLink>
        </div>
      </div>
    </section>
  );
};

const withDescription = (slice: WithDescriptionSlice) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#0E1010] py-6 px-4"
    >
      <WithDescription slice={slice} />
    </section>
  );
};

export default HeadlineCtaCentered;
